import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import * as Sentry from '@sentry/ember';

const UNKNOWN_VERSION = 'version-not-found';

export default class VersionCheckerService extends Service {
  @service tracker;

  version_name = '';
  @tracked app_should_update = false;

  /**
   * The release this tab booted with. Deliberately held in memory rather than
   * `localStorage`: that key is shared by every tab on the origin, so opening a
   * second tab after a deploy overwrote it with the *new* release, and the old
   * tab then compared latest-against-latest forever and could never flag itself
   * as stale. Each tab gets its own service instance, so an instance field is
   * per-tab by construction.
   */
  bootVersion = null;

  // Rapid navigation can ask for several checks at once; share one request.
  checkInProgress = null;

  /**
   * The deployed release name, or `null` when it cannot be determined.
   *
   * `null` matters: a failed or malformed read must never look like "the version
   * changed", or the app reloads on every route change forever. `amplify.yml`
   * writes a single `RELEASE_NAME=<name>` line.
   */
  async fetchVersion() {
    let text = '';
    try {
      const response = await fetch(`${window.location.origin}/version.txt`, {
        cache: 'no-store',
      });
      if (response.status === 200) text = await response.text();
    } catch (err) {
      // A same-origin fetch failure with no HTTP status is indistinguishable
      // from "the browser cancelled this because the tab navigated away/closed
      // mid-request" unless we record what the tab was doing at the time —
      // tagging visibility lets a real outage (still visible) be told apart
      // from routine navigation churn (hidden) without discarding either.
      Sentry.captureException(err, {
        tags: { page_visibility: document.visibilityState },
      });
      return null;
    }
    const [, name] = text.trim().split('=');
    return name || null;
  }

  async setup() {
    this.bootVersion = await this.fetchVersion();
    this.version_name = this.bootVersion ?? UNKNOWN_VERSION;
    this.setupTabFocusListener();
    this.tracker.setVersion(this.version_name);
    return this.version_name;
  }

  async checkIfAppShouldUpdate() {
    if (this.checkInProgress) return this.checkInProgress;

    this.checkInProgress = this._compareAgainstBootVersion().finally(() => {
      this.checkInProgress = null;
    });
    return this.checkInProgress;
  }

  async _compareAgainstBootVersion() {
    // Nothing to compare against — `setup()` never got a readable version.
    if (!this.bootVersion) return false;

    const latest = await this.fetchVersion();
    if (!latest) return false;

    this.app_should_update = latest !== this.bootVersion;
    return this.app_should_update;
  }

  setupTabFocusListener() {
    // A bound field, not an inline arrow: the old listener could never be
    // removed, so every service teardown leaked one.
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    // Only ever *flags* the update. Reloading here would throw away a form the
    // user tabbed away from mid-edit to copy something.
    return this.checkIfAppShouldUpdate();
  };

  willDestroy() {
    super.willDestroy(...arguments);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
  }
}
